import * as constants from "../constants/CONSTANT";
import helper from "../components/common/helper";

// In-memory GET cache to avoid refetching on page navigation/remount.
// This is intentionally module-scoped so it survives route changes.
const __getCache = new Map();

function __cacheKey(url) {
  return String(url || "");
}

function __isFresh(entry) {
  if (!entry) return false;
  if (entry.ttlMs === Infinity) return true;
  return Date.now() - entry.ts < entry.ttlMs;
}

async function __cachedGetJson(url, { ttlMs = Infinity } = {}) {
  const key = __cacheKey(url);
  const existing = __getCache.get(key);
  if (__isFresh(existing)) {
    return existing.dataPromise;
  }

  const dataPromise = (async () => {
    const response = await helper.fetchWithAuth(url, "GET");
    const json = await response.json();
    return json;
  })();

  __getCache.set(key, { ts: Date.now(), ttlMs, dataPromise });
  try {
    return await dataPromise;
  } catch (e) {
    // Don't keep failed responses in cache.
    __getCache.delete(key);
    throw e;
  }
}

function __invalidateGetCache(predicate) {
  for (const key of __getCache.keys()) {
    if (predicate(key)) __getCache.delete(key);
  }
}

const WhatsAppAPI = {
  invalidateCacheAll() {
    __getCache.clear();
  },

  invalidateChatCache() {
    __invalidateGetCache((key) => key.includes("/api/whatsapp/chat/"));
  },

  async fetchUsers() {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/auth/users",
      "GET",
    );
    const result = await response.json();
    if (result.length > 0) {
      return result;
    }
    return null;
  },

  //.............. Fetch Lead By Id .............................
  async fetchUserById(id, tenant) {
    id = id.trim();
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/auth/users/" + id + "/" + tenant,
      "GET",
    );

    const result = await response.json();
    return result;
  },

  async fetchTasksWithoutParent() {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/tasks/opentasks",
      "GET",
    );
    const result = await response.json();
    if (result.length > 0) {
      return result;
    }
    return null;
  },

  async sendEmailTask(task) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/tasks/sendemail",
      "POST",
      JSON.stringify(task),
    );
    return await response.json();
  },

  async fetchLeadReports(reportname) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/reports/byname/" + reportname,
      "GET",
    );
    const result = await response.json();
    return result;
  },

  async getCurrentUserTrackingRec() {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/usertrackings/track/currentrecord",
      "GET",
    );
    const result = await response.json();
    if (result) {
      return result;
    }
    return null;
  },

  async saveStaffMemberEditProfile(userid, selectedFiles, staffMember) {
    const formData = new FormData();
    formData.append("file", selectedFiles);
    formData.append("staffRecord", staffMember);
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/auth/" + userid + "/profile",
      "PUT",
      formData,
      "form",
    );
    return await response.json();
  },

  async createCheckInRecord(locationRecord) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/usertrackings",
      "POST",
      JSON.stringify(locationRecord),
    );
    const result = await response.json();
    if (result) {
      return result;
    }
    return null;
  },

  async fetchUsertrackingsWithstaffId(staffId) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/usertrackings/staff/" + staffId,
      "GET",
    );
    const result = await response.json();
    if (result.length > 0) {
      return result;
    }
    return null;
  },

  async handlCheckOut(locationRecord) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/usertrackings/" + locationRecord.id,
      "PUT",
      JSON.stringify(locationRecord),
    );
    const result = await response.json();
    if (result) {
      return result;
    }
    return null;
  },

  async getLoginUserData() {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/auth/getuser",
      "GET",
    );
    const result = await response.json();
    return result;
  },

  // working
  async fetchCompanySetting(settingName) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/whatsapp/common/settings/" + settingName,
      "GET",
    );
    const result = await response.json();
    return result;
  },

  async updateUser(user) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/auth/updatepassword",
      "PUT",
      JSON.stringify(user),
    );
    return await response.json();
  },

  //************************ Accounts ***********************************//
  //.................... Crate Account ...................................
  async createAccount(account) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/accounts",
      "POST",
      JSON.stringify(account),
    );
    return await response.json();
  },

  async saveAccount(account) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/accounts/" + account.id,
      "PUT",
      JSON.stringify(account),
    );
    return await response.json();
  },

  //.......... Fetch All Accounts ..........................................
  async fetchAccounts() {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/accounts",
      "GET",
    );
    const result = await response.json();
    if (result.length > 0) {
      return result;
    }
    return null;
  },

  //.............. Fetch Account By Id .............................
  async fetchAccountById(id) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/accounts/" + id,
      "GET",
    );
    const result = await response.json();
    return result;
  },

  //............. Delete Account ............................
  async deleteAccount(id) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/accounts/" + id,
      "DELETE",
    );
    return await response.json();
  },

  async createUser(user) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/auth/createuser",
      "POST",
      JSON.stringify(user),
    );
    return await response.json();
  },

  async saveUser(user) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/auth/" + user.id,
      "PUT",
      JSON.stringify(user),
    );
    return await response.json();
  },

  //****************** File  *******************
  async createFile(id, formData) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/whatsapp/files/" + id,
      "POST",
      formData,
      "form",
    );
    return await response.json();
  },

  async cloneCampaignFile(fileData) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/whatsapp/files/clone",
      "POST",
      JSON.stringify(fileData),
    );
    return await response.json();
  },
  //************************ files ***********************************//
  async saveFiles(file) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/files/" + file.id,
      "PUT",
      JSON.stringify(file),
    );
    return await response.json();
  },

  async deleteFile(id) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/files/" + id,
      "DELETE",
    );
    return await response.json();
  },

  async getFilterData(textName, recordType) {
    const params = new URLSearchParams({
      textName: textName || "",
      recordType: recordType || "",
    }).toString();
    return await __cachedGetJson(
      `${constants.API_BASE_URL}/api/whatsapp/chat/filter?${params}`,
    );
  },

  /**
   * Lazy-load filter: fetches records in chunks (e.g. 50) for pagination/infinite scroll.
   * @param {string} textName - search filter
   * @param {string} recordType - lead | archived | unread | recentlyMessage etc.
   * @param {number} limit - page size (default 50)
   * @param {number} offset - skip count
   * @returns {Promise<{ success, records, total, hasMore }>}
   */
  async getFilterDataLazy(textName, recordType, limit = 50, offset = 0) {
    const params = new URLSearchParams({
      textName: textName || "",
      recordType: recordType || "",
      limit: String(limit),
      offset: String(offset),
    }).toString();
    return await __cachedGetJson(
      `${constants.API_BASE_URL}/api/whatsapp/chat/lazyfilter?${params}`,
    );
  },

  /**
   * Messenger conversation list (paginated). POST so tag UUID arrays are sent cleanly.
   */
  async getConversations({
    textName = "",
    recordType = "",
    limit = 50,
    offset = 0,
    tagIds = [],
    tagFilterLogic = "OR",
  } = {}) {
    const body = {
      textName: textName || "",
      recordType: recordType || "",
      limit,
      offset,
      tagFilterLogic,
    };
    if (Array.isArray(tagIds) && tagIds.length > 0) {
      body.tagIds = tagIds;
    }
    const response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/whatsapp/chat/conversations`,
      "POST",
      JSON.stringify(body),
    );
    return await response.json();
  },

  async fetchUnreadMsgCount(number) {
    if (!number || number.trim() === "") {
      return [];
    }

    const url = `${constants.API_BASE_URL}/api/whatsapp/chat/unread_count?whatsapp_setting_number=${number}`;
    return await __cachedGetJson(url, { ttlMs: 10 * 1000 });
  },

  async markMessagesAsRead(whatsappNumber, number) {
    if (!number || number.trim() === "") {
      return [];
    }

    let response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/whatsapp/chat/mark_as_read?whatsapp_setting_number=${number}`,
      "POST",
      JSON.stringify({ whatsapp_number: whatsappNumber }),
    );
    const result = await response.json();
    // unread_count + lists can change
    WhatsAppAPI.invalidateChatCache();
    return result;
  },

  //************************* WHATSAPP SETTING || START *******************************************************************//
  async getWhatsAppSettingRecord() {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/whatsapp_setting",
      "GET",
    );
    const result = await response.json();
    return result;
  },

  async insertWhatsAppSettingRecords(reqbody) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/whatsapp_setting",
      "POST",
      JSON.stringify(reqbody),
    );
    return await response.json();
  },

  async updateWhatsAppSettingRecord(data) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/whatsapp_setting/" + data.id,
      "PUT",
      JSON.stringify(data),
    );
    return await response.json();
  },

  //************* WHATSAPP SETTING || END *****//

  /******Template || START ************************ */
  async getTemplateaAmountBySetting(
    template_id,
    selectedWhatsAppSetting,
    start,
    end,
  ) {
    let response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/whatsapp_setting/templateprice/${template_id}/${selectedWhatsAppSetting}/${start}/${end}`,
      "GET",
    );
    // console.log('resp: ', await response.json())
    return await response.json();
  },

  async getAllTemplates(number) {
    if (!number || number.trim() === "") {
      return [];
    }

    let response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/webhook_template/alltemplate?whatsapp_setting_number=${number}`,
      "GET",
    );
    return await response.json();
  },

  async getTemplateByName(name, number) {
    if (!number || number.trim() === "") {
      return [];
    }
    if (!name || name.trim() === "") {
      return [];
    }

    let response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/webhook_template/templatebyname?name=${name}&whatsapp_setting_number=${number}`,
      "GET",
    );
    return await response.json();
  },

  async getApprovedTemplates(number) {
    if (!number || number.trim() === "") {
      return [];
    }

    let response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/webhook_template/approved/template?whatsapp_setting_number=${number}`,
      "GET",
    );
    return await response.json();
  },

  async generateSessionId(file, number) {
    if (!number || number.trim() === "") {
      return [];
    }

    const formData = new FormData();
    formData.append("files", file);

    let response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/webhook_template?whatsapp_setting_number=${number}`,
      "POST",
      formData,
      "form",
    );
    return await response.json();
  },

  async uploadDocumentSessionId(file, sessionId, number) {
    if (!number || number.trim() === "") {
      return [];
    }

    const formData = new FormData();
    formData.append("files", file);
    formData.append("uploadSessionId", sessionId);

    let response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/webhook_template/uploadsessionid?whatsapp_setting_number=${number}`,
      "POST",
      formData,
      "form",
    );
    return await response.json();
  },

  async createMarketingTemplate(reqbody, number) {
    if (!number || number.trim() === "") {
      return [];
    }
    let response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/webhook_template/template?whatsapp_setting_number=${number}`,
      "POST",
      JSON.stringify(reqbody),
    );
    return await response.json();
  },

  async updateMarketingTemplate(id, reqbody, number) {
    if (!number || number.trim() === "") {
      return [];
    }
    let response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/webhook_template/template/${id}?whatsapp_setting_number=${number}`,
      "POST",
      JSON.stringify(reqbody),
    );
    return await response.json();
  },

  async upsertAuthTemplate(reqbody, number) {
    if (!number || number.trim() === "") {
      return [];
    }
    let response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/webhook_template/temp/auth?whatsapp_setting_number=${number}`,
      "POST",
      JSON.stringify(reqbody),
    );
    return await response.json();
  },
  // Delete Template
  async deleteTemplateRecord(id, name, number) {
    if (!number || number.trim() === "") {
      return [];
    }
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL +
        "/api/webhook_template/template?hsm_id=" +
        id +
        "&name=" +
        name +
        "&whatsapp_setting_number=" +
        number,
      "DELETE",
    );
    const result = await response.json();
    return result;
  },

  /****************************Template || END ************************************************* */

  //************************* CREATE TEMPLATE IN DATABASE || START *********************************//
  async createMessageTemplateData(reqbody) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/whatsapp/message/template",
      "POST",
      JSON.stringify(reqbody),
    );
    return await response.json();
  },

  // delete template database record
  async deleteTemplateDatabase(id) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/whatsapp/message/template/" + id,
      "DELETE",
    );
    return await response.json();
  },

  //************************* CREATE TEMPLATE IN DATABASE || END **********************************************//

  //************************* CREATE Campaign Records || START **********************************************//
  async getCampaignData(number) {
    if (!number || number.trim() === "") {
      return [];
    }
    let response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/whatsapp/campaign?whatsapp_setting_number=${number}`,
      "GET",
    );
    const result = await response.json();
    return result;
  },

  /** Single campaign (e.g. track page URL /campaign/track/:id without navigation state). */
  async getCampaignById(id) {
    if (!id) {
      return { success: false, error: "Missing campaign id" };
    }
    const response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/whatsapp/campaign/${id}`,
      "GET",
    );
    return await response.json();
  },

  async insertCampaignRecords(reqbody) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/whatsapp/campaign",
      "POST",
      JSON.stringify(reqbody),
    );
    return await response.json();
  },

  async updateCampaignRecord(data) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/whatsapp/campaign/" + data.id,
      "PUT",
      JSON.stringify(data),
    );
    return await response.json();
  },

  async downloadCampaignFile(filename) {
    try {
      let response = await helper.fetchWithAuth(
        constants.API_BASE_URL + "/api/whatsapp/campaign/download/" + filename,
        "GET",
      );

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        } else if (response.status === 401) {
          throw new Error("Unauthorized access. Please login.");
        } else {
          throw new Error(
            "Failed to download file. Status: " + response.status,
          );
        }
      }

      const fileBody = await response.blob();
      return fileBody;
    } catch (error) {
      // console.error('Error downloading file:', error);
      throw error; // Re-throw error for further handling if needed
    }
  },

  async getMsgHistoryPercenatge(id) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/whatsapp/message/percentage/" + id,
      "GET",
    );
    const result = await response.json();
    return result;
  },

  async getMsgHistoryByStatus(id, status) {
    let response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/whatsapp/message/status?id=${id}&status=${status}`,
      "GET",
    );
    const result = await response.json();
    return result;
  },

  async getMsgHistoryDownload(id) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/whatsapp/message/history/download/" + id,
      "GET",
    );
    const result = await response.json();
    return result;
  },

  async deleteCampaignRecord(id) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/whatsapp/campaign/" + id,
      "DELETE",
    );
    return await response.json();
  },

  async createCampaignFile(id, formData) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/whatsapp/campaign/file/" + id,
      "POST",
      formData,
      "form",
    );
    return await response.json();
  },

  //************************* CREATE Campaign Records || END **********************************************//

  //************************* SEND WHATSAPP MESSAGE || START **********************************************//
  async sendWhatsAppTemplateMessage(reqbody, number, category) {
    if (!number || number.trim() === "") {
      return [];
    }
    let response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/webhook_template/message?whatsapp_setting_number=${number}&category=${category}`,
      "POST",
      JSON.stringify(reqbody),
    );
    return await response.json();
  },

  async sendWhatsAppTemplate(reqbody, number) {
    if (!number || number.trim() === "") {
      return [];
    }
    let response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/webhook_template/send?whatsapp_setting_number=${number}`,
      "POST",
      JSON.stringify(reqbody),
    );
    return await response.json();
  },

  async sendSingleWhatsAppTextMessage(reqbody, number) {
    if (!number || number.trim() === "") {
      return [];
    }

    let response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/webhook_template/single/message?whatsapp_setting_number=${number}`,
      "POST",
      JSON.stringify(reqbody),
    );
    return await response.json();
  },
  // upload Documents get id
  async uploadDocumentWithApi(file, number) {
    if (!number || number.trim() === "") {
      return [];
    }
    let response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/webhook_template/documentId?whatsapp_setting_number=${number}`,
      "POST",
      file,
      "form",
    );
    return await response.json();
  },

  async fetchDocumentId(reqbody, number) {
    if (!number || number.trim() === "") {
      return [];
    }
    let response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/webhook_template/upload_whatsapp_media?whatsapp_setting_number=${number}`,
      "POST",
      JSON.stringify(reqbody),
    );
    return await response.json();
  },

  //************************* SEND WHATSAPP MESSAGE || END **********************************************//

  //************************* CREATE MESSAGE HISTORY || START **********************************************//
  async getMsgHistoryRecords(number, business_number, options = {}) {
    if (!business_number || business_number.trim() === "") {
      return [];
    }
    const params = new URLSearchParams({
      whatsapp_setting_number: business_number,
    });
    if (options.limit != null && options.limit !== "") {
      params.set("limit", String(options.limit));
    }
    if (options.offset != null && options.offset !== "") {
      params.set("offset", String(options.offset));
    }
    let response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/whatsapp/message/history/${encodeURIComponent(number)}?${params.toString()}`,
      "GET",
    );
    const result = await response.json();
    return result;
  },

  async insertMsgHistoryRecords(reqbody) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/whatsapp/message/history",
      "POST",
      JSON.stringify(reqbody),
    );
    return await response.json();
  },

  async getGroupHistoryRecords(id, number) {
    //
    if (!number || number.trim() === "") {
      return [];
    }

    let response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/whatsapp/group/message/history/${id}?whatsapp_setting_number=${number}`,
      "GET",
    );
    const result = await response.json();
    return result;
  },

  async historyDelete(id, number) {
    if (!number || number.trim() === "") {
      return [];
    }
    let response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/whatsapp/history/${id}?whatsapp_setting_number=${number}`,
      "DELETE",
    );
    return await response.json();
  },

  //************************* CREATE MESSAGE HISTORY || END **********************************************//

  // *******response Message || START ***********************************
  async getResponseMessageData() {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/response_message",
      "GET",
    );
    const result = await response.json();
    return result;
  },

  async inserResponseMessageRecord(reqbody) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/response_message",
      "POST",
      JSON.stringify(reqbody),
    );
    return await response.json();
  },

  async updateResponseMessageRecord(data) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/response_message/" + data.id,
      "PUT",
      JSON.stringify(data),
    );
    return await response.json();
  },

  async deleteResponseMessageRecord(id) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/response_message/" + id,
      "DELETE",
    );
    return await response.json();
  },
  // *******response Message || END ***********************************

  //************START || GROUP  **************************************************/
  async fetchGroupsById(id) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/whatsapp/groups/" + id,
      "GET",
    );
    return await response.json();
  },

  async fetchGroups(status) {
    const params = new URLSearchParams({ status: status || "" }).toString();

    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/whatsapp/groups?" + params,
      "GET",
    );
    const result = await response.json();
    return result;
  },
  async createGroup(formData) {
    try {
      let response = await helper.fetchWithAuth(
        constants.API_BASE_URL + "/api/whatsapp/groups",
        "POST",
        formData,
        "form",
      );
      const jsonResponse = await response.json();

      if (!response.ok) {
        throw new Error(`Error: ${jsonResponse.message || "Unknown error"}`);
      }

      return jsonResponse;
    } catch (error) {
      //    console.log("Failed to create group:", error);
      throw error;
    }
  },
  async addMoreMembers(groupId, formData) {
    try {
      let response = await helper.fetchWithAuth(
        constants.API_BASE_URL + "/api/whatsapp/groups/add_members/" + groupId,
        "POST",
        formData,
        "form",
      );
      const jsonResponse = await response.json();

      if (!response.ok) {
        throw new Error(`Error: ${jsonResponse.message || "Unknown error"}`);
      }

      return jsonResponse;
    } catch (error) {
      //    console.log("Failed to create group:", error);
      throw error;
    }
  },

  async deleteGroupMember(member_id) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/whatsapp/groups/member/" + member_id,
      "DELETE",
    );
    return await response.json();
  },

  async changeGroupStatus(group_id, status) {
    let response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/whatsapp/groups/${group_id}/status`,
      "PUT",
      JSON.stringify({ status }),
    );
    return await response.json();
  },

  async fetchLead(params = {}) {
    const q = new URLSearchParams();
    if (params.page != null) q.set("page", String(params.page));
    if (params.pageSize != null) q.set("pageSize", String(params.pageSize));
    if (params.q) q.set("q", params.q);
    if (params.status) q.set("status", params.status);
    if (params.tags) q.set("tags", params.tags);
    if (params.agents) q.set("agents", params.agents);
    const qs = q.toString();
    const url = constants.API_BASE_URL + "/api/leads" + (qs ? `?${qs}` : "");
    let response = await helper.fetchWithAuth(url, "GET");
    if (response.ok) {
      const result = await response.json();
      return result;
    }
    return null;
  },

  async fetchLeadTagOptions() {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/leads/tag-options",
      "GET",
    );
    if (response.ok) {
      return await response.json();
    }
    return null;
  },

  async fetchLeadById(id) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/leads/" + id,
      "GET",
    );

    const result = await response.json();

    return result;
  },

  async createLead(lead) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/leads",
      "POST",
      JSON.stringify(lead),
    );
    return await response.json();
  },

  async importLeads(lead) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/leads/import",
      "POST",
      JSON.stringify(lead),
    );
    return await response.json();
  },

  async updateLead(lead) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/leads/" + lead.id,
      "PUT",
      JSON.stringify(lead),
    );
    return await response.json();
  },

  async deleteLead(id) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/leads/" + id,
      "DELETE",
    );
    return await response.json();
  },

  async fetchBlockedLeads() {
    console.log("res");
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/leads/blocked",
      "GET",
    );
    if (response.ok) {
      const result = await response.json();
      return result;
    }
    return null;
  },

  // **************Lead || END ************************************

  async fetchHomeSummary(business_number) {
    if (!business_number || String(business_number).trim() === "") {
      return { success: false, data: null };
    }
    let response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/whatsapp/common/dashboard?business_number=${encodeURIComponent(business_number)}`,
      "GET",
    );
    return await response.json();
  },

  async fetchallActiveGroups() {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/whatsapp/common/activegroups",
      "GET",
    );
    const result = await response.json();
    return result;
  },

  async fetchChatGptResponse(prompt) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/whatsapp/common/chatgpt",
      "POST",
      JSON.stringify({ prompt }),
    );

    if (!response.ok) {
      const errorText = await response.text(); // Get error message from response
      return {
        success: false,
        message: errorText || "Failed to get ChatGPT response",
      };
    }

    const data = await response.json();
    return data;
  },

  async getRazorPayData(obj) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/whatsapp/online_payment",
      "POST",
      JSON.stringify(obj),
    );
    if (!response.ok) {
      throw new Error(`Error fetching Razorpay data: ${response.statusText}`);
    }
    const data = await response.json();
    return data;
  },

  async pdfData(obj, number) {
    if (!number || number.trim() === "") {
      return [];
    }
    let response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/webhook_template/proxy?whatsapp_setting_number=${number}`,
      "POST",
      JSON.stringify(obj),
    );
    return await response.json();
  },

  async mediaData(obj, number) {
    if (!number || number.trim() === "") {
      return [];
    }
    let response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/webhook_template/upload_whatsapp_media?whatsapp_setting_number=${number}`,
      "POST",
      JSON.stringify(obj),
    );
    return await response.json();
  },

  async activateWhatsAppSetting(setting_id) {
    let response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/whatsapp_setting/activate/${setting_id}`,
      "PUT",
    );
    return await response.json();
  },

  async getModuleData(status = null) {
    const queryParam = status ? `?status=${status}` : "";

    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + `/api/whatsapp/module${queryParam}`,
      "GET",
    );
    const result = await response.json();
    return result;
  },

  async insertModuleRecord(reqbody) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/whatsapp/module",
      "POST",
      JSON.stringify(reqbody),
    );
    return await response.json();
  },

  async updateModuleRecord(data) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/whatsapp/module/" + data.id,
      "PUT",
      JSON.stringify(data),
    );
    return await response.json();
  },

  async deleteModuleRecord(id) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/whatsapp/module/" + id,
      "DELETE",
    );
    return await response.json();
  },

  async getPlanData(status = null) {
    const queryParam = status ? `?status=${status}` : "";

    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + `/api/whatsapp/plan${queryParam}`,
      "GET",
    );
    const result = await response.json();
    return result;
  },

  async getPlansById(id) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/whatsapp/plan/" + id,
      "GET",
    );
    const result = await response.json();
    return result;
  },

  async insertPlanRecord(reqbody) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/whatsapp/plan",
      "POST",
      JSON.stringify(reqbody),
    );
    return await response.json();
  },

  async updatePlanRecord(data) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/whatsapp/plan/" + data.plan_info.id,
      "PUT",
      JSON.stringify(data),
    );
    return await response.json();
  },

  async deletePlanRecord(id) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/whatsapp/plan/" + id,
      "DELETE",
    );
    return await response.json();
  },

  async fetchCompany(is_active = null) {
    const queryParam = is_active ? `?is_active=${is_active}` : "";

    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + `/api/company${queryParam}`,
      "GET",
    );
    const result = await response.json();
    return result;
  },

  async fetchSourceSchemas() {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/company/all/getschema",
      "GET",
    );
    const result = await response.json();
    return result;
  },

  async createCompany(companyData) {
    const formData = new FormData();

    formData.append("logo", companyData.company_info.logourl);
    formData.append("request", JSON.stringify(companyData));

    let files = [companyData.company_info.logourl];
    formData.append("files", files);

    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/company",
      "POST",
      formData,
      "form",
    );
    return await response.json();
  },

  async findCompanyWithUser(id) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/company/detail/" + id,
      "GET",
    );
    const result = await response.json();
    return result;
  },

  async updateCompanyWithUser(companyData) {
    const formData = new FormData();

    formData.append("logo", companyData.company_info.logourl);
    formData.append("request", JSON.stringify(companyData));
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/company/updateCompanyWithUser",
      "PUT",
      formData,
      "form",
    );
    return await response.json();
  },

  async getCompanyRecordsById(id) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/company/" + id,
      "GET",
    );
    const result = await response.json();
    return result;
  },

  async updateCompany(records) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/company/" + records.id,
      "PUT",
      JSON.stringify(records),
    );
    return await response.json();
  },

  async getInvoicesRecord(status) {
    const queryParam = status ? `?status=${status}` : "";

    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + `/api/invoice${queryParam}`,
      "GET",
    );
    const result = await response.json();
    return result;
  },

  async fetchCompanyAndUserByInvoice(id) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/invoice/i/" + id,
      "GET",
    );
    const result = await response.json();
    return result;
  },

  async getInvoicesByCompanyId(companyId) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/invoice/company/" + companyId,
      "GET",
    );
    const result = await response.json();
    return result;
  },

  async getInvoiceById(id) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/invoice/" + id,
      "GET",
    );
    const result = await response.json();
    return result;
  },

  async updateInvoiceRecord(records) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/invoice/" + records.id,
      "PUT",
      JSON.stringify(records),
    );
    return await response.json();
  },

  async addInvoiceWithTransaction(records) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/invoice/invoiceWithTransaction",
      "POST",
      JSON.stringify(records),
    );
    return await response.json();
  },

  async updateInvoiceAddTransaction(records) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL +
        "/api/invoice/updateInvoiceAddTrans/" +
        records.id,
      "PUT",
      JSON.stringify(records),
    );
    return await response.json();
  },

  async duplicateEmailCheck(email, id) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/company/emailcheck",
      "POST",
      JSON.stringify({ email, id }),
    );
    return await response.json();
  },

  async getAllLeads() {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + `/api/publicleads`,
      "GET",
    );
    const result = await response.json();
    return result;
  },

  async createPublicLead(lead) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/publicleads",
      "POST",
      JSON.stringify(lead),
    );
    return await response.json();
  },

  async updatePublicLead(lead) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/publicleads/" + lead.id,
      "PUT",
      JSON.stringify(lead),
    );
    return await response.json();
  },

  async deletePublicLead(id) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/publicleads/" + id,
      "DELETE",
    );
    return await response.json();
  },

  async sendInvoiceMail(imgData, invoiceData) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/mail/invoice/",
      "POST",
      JSON.stringify({ imgData, invoiceData }),
    );
    // console.log('resp: ', await response.json())
    return await response.json();
  },

  async getSetting(name) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + `/api/whatsapp/common/setting/` + name,
      "GET",
    );
    const result = await response.json();
    return result;
  },

  async getBillingCostsBySetting(selectedWhatsAppSetting, start, end) {
    let response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/whatsapp_setting/billing-cost/${selectedWhatsAppSetting}/${start}/${end}`,
      "GET",
    );
    // console.log('resp: ', await response.json())
    return await response.json();
  },

  async insertCampaignParamsRecords(reqbody) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/whatsapp/campaign/params",
      "POST",
      JSON.stringify(reqbody),
    );
    return await response.json();
  },

  async fetchInteractiveMessage(number) {
    if (!number || number.trim() === "") {
      return [];
    }

    let response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/whatsapp/interactive_message?whatsapp_setting_number=${number}`,
      "GET",
    );
    const result = await response.json();
    return result;
  },
  async createInteractiveMessage(reqbody, number) {
    if (!number || number.trim() === "") {
      return [];
    }

    let response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/whatsapp/interactive_message?whatsapp_setting_number=${number}`,
      "POST",
      JSON.stringify(reqbody),
    );
    return await response.json();
  },

  async updateInteractiveMessage(reqbody, number) {
    if (!number || number.trim() === "") {
      return [];
    }
    let response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/whatsapp/interactive_message?whatsapp_setting_number=${number}`,
      "PUT",
      JSON.stringify(reqbody),
    );
    return await response.json();
  },

  async fetchChatbots(number) {
    if (!number || number.trim() === "") {
      return [];
    }
    let response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/whatsapp/chatbot?whatsapp_setting_number=${number}`,
      "GET",
    );
    return await response.json();
  },

  async fetchChatbotById(id) {
    let response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/whatsapp/chatbot/${id}`,
      "GET",
    );
    return await response.json();
  },

  async insertChatbotRecord(reqbody, number) {
    if (!number || number.trim() === "") {
      return [];
    }
    let response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/whatsapp/chatbot?whatsapp_setting_number=${number}`,
      "POST",
      JSON.stringify(reqbody),
    );
    return await response.json();
  },

  async updateChatbotRecord(id, reqbody) {
    let response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/whatsapp/chatbot/${id}`,
      "PUT",
      JSON.stringify(reqbody),
    );
    return await response.json();
  },

  async deleteChatbotRecord(id) {
    let response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/whatsapp/chatbot/${id}`,
      "DELETE",
    );
    return await response.json();
  },

  async getAllAddons() {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + `/api/whatsapp/addons`,
      "GET",
    );
    const result = await response.json();
    // console.log("response", result);
    return result;
  },

  async createAddon(addon) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/whatsapp/addons",
      "POST",
      JSON.stringify(addon),
    );
    return await response.json();
  },

  async updateAddon(addon) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/whatsapp/addons/" + addon.id,
      "PUT",
      JSON.stringify(addon),
    );
    return await response.json();
  },

  async deleteAddonRecord(id) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/whatsapp/addons/" + id,
      "DELETE",
    );
    return await response.json();
  },

  async generateInvoiceAddTransaction(records) {
    // console.log("🧾 Final Payload → records", records);
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL +
        "/api/invoice/generateInvoice/AddTrans/" +
        records.invoice_id,
      "PUT",
      JSON.stringify(records),
    );
    return await response.json();
  },

  // Save WABA info after signup
  async saveWABAInfo(payload) {
    const response = await fetch(
      `${constants.API_BASE_URL}/api/whatsapp_setting/waba-info`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      },
    );
    return await response.json();
  },

  async exchangeAccessToken(code) {
    const response = await fetch(
      `${constants.API_BASE_URL}/api/whatsapp_setting/exchange-token`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ code }),
      },
    );
    return await response.json();
  },

  // Get lead reports
  async getLeadReports(filters = {}) {
    const params = new URLSearchParams();
    if (filters.startDate) params.append("startDate", filters.startDate);
    if (filters.endDate) params.append("endDate", filters.endDate);
    if (filters.ownerId) params.append("ownerId", filters.ownerId);
    if (filters.status) params.append("status", filters.status);

    const response = await helper.fetchWithAuth(
      `${
        constants.API_BASE_URL
      }/api/whatsapp/common/reports?${params.toString()}`,
      "GET",
    );
    // console.log("🚀 ~ getLeadReports ~ response:", response);
    return await response.json();
  },

  async getTags(status = null) {
    const queryParam = status ? `?status=${status}` : "";
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + `/api/whatsapp/tag${queryParam}`,
      "GET",
    );
    const result = await response.json();
    return result;
  },

  async createTag(tag) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/whatsapp/tag",
      "POST",
      JSON.stringify(tag),
    );
    return await response.json();
  },

  async updateTag(id, tag) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/whatsapp/tag/" + id,
      "PUT",
      JSON.stringify(tag),
    );
    return await response.json();
  },

  async getTagById(id) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL +
        "/api/whatsapp/tag/" +
        id +
        "?include_rules=true",
      "GET",
    );
    return await response.json();
  },

  async getWalletTransactions() {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + `/api/whatsapp/wallet/transactions`,
      "GET",
    );
    const result = await response.json();
    return result;
  },

  async createWalletTransaction(payload) {
    const response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/whatsapp/wallet`,
      "POST",
      JSON.stringify(payload),
    );
    return await response.json();
  },

  async getWalletBalance() {
    const response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/whatsapp/wallet/balance`,
      "GET",
    );
    return await response.json();
  },

  async updateWalletTransaction(id, payload) {
    const response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/whatsapp/wallet/${id}`,
      "PUT",
      JSON.stringify(payload),
    );
    return await response.json();
  },

  async templateRates() {
    const response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/whatsapp/template_rates`,
      "GET",
    );
    return await response.json();
  },

  // ✅ WhatsAppAPI.js
  // ✅ WhatsAppAPI.js
  async updateTemplateStatus(data) {
    const response = await helper.fetchWithAuth(
      constants.API_BASE_URL + `/api/whatsapp/template/${data.id}/status`,
      "PUT",
      JSON.stringify({
        status: data.status,
        userid: data.userid,
      }),
    );
    return await response.json();
  },

  async checkBalance(payload) {
    // console.log("payload",payload)
    const response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/whatsapp/wallet/check_balance`,
      "POST",
      JSON.stringify(payload),
    );
    return await response.json();
  },

  async pinLead(leadId) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/leads/" + leadId + "/pin",
      "POST",
      undefined,
    );
    const result = await response.json();
    WhatsAppAPI.invalidateChatCache();
    return result;
  },

  async unpinLead(leadId) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/leads/" + leadId + "/unpin",
      "POST",
      undefined,
    );
    const result = await response.json();
    WhatsAppAPI.invalidateChatCache();
    return result;
  },

  async getCallLogs(business_number, whatsapp_number) {
    const params = new URLSearchParams({
      business_number: business_number || "",
      whatsapp_number: whatsapp_number || "",
    }).toString();
    let response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/whatsapp/call?${params}`,
      "GET",
    );
    const result = await response.json();
    return result;
  },

  //   async getCallPermission(business_number, whatsapp_number) {
  //   const params = new URLSearchParams({
  //     business_number: business_number || "",
  //     whatsapp_number: whatsapp_number || "",
  //   }).toString();

  //   const response = await helper.fetchWithAuth(
  //     `${constants.API_BASE_URL}/api/whatsapp/call/call_permission?${params}`,
  //     "GET"
  //   );

  //   const result = await response.json();
  //   return result;
  // },

  async insertCall(payload) {
    const url = `${constants.API_BASE_URL}/api/whatsapp/call`;
    const response = await helper.fetchWithAuth(
      url,
      "POST",
      JSON.stringify(payload),
    );
    return await response.json();
  },

  async acceptCall(payload, business_number) {
    const url = `${constants.API_BASE_URL}/api/whatsapp/call/accept`;
    const response = await helper.fetchWithAuth(
      url,
      "POST",
      JSON.stringify({ payload, business_number }),
    );
    return await response.json();
  },

  async rejectCall(payload) {
    const url = `${constants.API_BASE_URL}/api/whatsapp/call/reject`;
    const response = await helper.fetchWithAuth(
      url,
      "POST",
      JSON.stringify(payload),
    );
    return await response.json();
  },

  async getWabas() {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/whatsapp_setting/wabas",
      "GET",
    );
    const result = await response.json();
    return result;
  },

  async getAllWhatsAppSettingRecord() {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/whatsapp_setting/all_settings",
      "GET",
    );
    const result = await response.json();
    return result;
  },

  async insertWhatsAppSetting(reqbody) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/whatsapp_setting/schema_settings",
      "POST",
      JSON.stringify(reqbody),
    );
    return await response.json();
  },

  // Bulk Delete
  async bulkDeleteLeads({ ids } = {}) {
    if (!Array.isArray(ids) || ids.length === 0) {
      throw new Error("No lead IDs provided for deletion");
    }

    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/leads/bulk-delete",
      "POST",
      JSON.stringify({ ids }),
      { "Content-Type": "application/json" },
    );

    return await response.json();
  },

  async exportMessageHistory(business_number, whatsapp_number = null) {
    const url = `${
      constants.API_BASE_URL
    }/api/whatsapp/export/${business_number}${
      whatsapp_number ? `?whatsapp_number=${whatsapp_number}` : ""
    }`;
    const response = await helper.fetchWithAuth(url, "GET");
    return await response.json();
  },

  async bulkDeleteLeads({ ids } = {}) {
    if (!Array.isArray(ids) || ids.length === 0) {
      throw new Error("No lead IDs provided for deletion");
    }

    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/leads/bulk-delete",
      "POST",
      JSON.stringify({ ids }),
      { "Content-Type": "application/json" },
    );

    return await response.json();
  },

  async fetchAssignedLeads() {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/leads/assigned",
      "GET",
    );
    const result = await response.json();
    return result;
  },

  async deleteAllAssignedLeads(id) {
    let response = await helper.fetchWithAuth(
      constants.API_BASE_URL + "/api/leads/assigned/" + id,
      "DELETE",
    );
    return await response.json();
  },

  async getAdManagerRecords(business_number) {
    if (!business_number || business_number.trim() === "") {
      return [];
    }

    let response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/ad_manager?business_number=${business_number}`,
      "GET",
    );

    const result = await response.json();
    return result;
  },

  async assignAgentsToLeads(reqbody) {
    let response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/leads/assign-agents`,
      "POST",
      JSON.stringify(reqbody),
    );
    return await response.json();
  },

  async addTagsToLeads(reqbody) {
    let response = await helper.fetchWithAuth(
      `${constants.API_BASE_URL}/api/leads/bulk-add-tags`,
      "POST",
      JSON.stringify(reqbody),
    );
    return await response.json();
  },
};

// **************************

export default WhatsAppAPI;
